import React, { useEffect, useState, useCallback } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import axios from "axios";
import { triggerAlert } from "../../utils";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import Cookies from "js-cookie";

/**
 * Billing → Invoices (observe-only).
 *
 * Lists billing.AgentSubscriptionInvoice via /api/admin/agent-invoices/.
 * Invoices are auto-generated per charge / ARB renewal — there is no manual
 * "create invoice" in the new billing model. Admins review and open the
 * rendered PDF.
 */

const STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "open", label: "Open" },
  { value: "failed", label: "Failed" },
  { value: "void", label: "Void" },
];

export default function Invoices() {
  document.title = "Digital Employees - Invoices";

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
      "Content-Type": "application/json",
    },
  });

  const fetchData = useCallback(
    async (page = 1, size = perPage) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({ page_number: page, page_size: size });
        if (statusFilter) params.append("status", statusFilter);
        if (search.trim()) params.append("search", search.trim());
        const res = await axios.get(
          `/api/admin/agent-invoices/?${params.toString()}`,
          authHeaders()
        );
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        data.forEach((r, i) => { r.Num = (page - 1) * size + i + 1; });
        setRows(data);
        setTotalRows(res.data?.count || data.length);
      } catch (err) {
        setRows([]);
        setTotalRows(0);
        triggerAlert("error", "Oops...", "Failed to load invoices.");
      } finally {
        setIsLoading(false);
      }
    },
    [perPage, statusFilter, search]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      fetchData(1, perPage);
    }, 400);
    return () => clearTimeout(t);
  }, [fetchData, perPage]);

  const viewPdf = async (row) => {
    try {
      const res = await axios.get(`/api/admin/agent-invoices/${row.id}/pdf/`, {
        ...authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      window.open(url, "_blank");
    } catch (err) {
      triggerAlert("error", "Oops...", "Could not open the invoice PDF.");
    }
  };

  const openPreview = async (row) => {
    // Fetch the PDF as a blob so the modal iframe carries the auth-scoped URL.
    setPreviewInvoice(row);
    setPreviewUrl(null);
    try {
      const res = await axios.get(`/api/admin/agent-invoices/${row.id}/pdf/`, {
        ...authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      setPreviewUrl(url);
    } catch (err) {
      triggerAlert("error", "Oops...", "Could not preview the invoice PDF.");
      setPreviewInvoice(null);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewInvoice(null);
  };

  const openEmail = (row) => {
    setEmailInvoice(row);
    // Prefill with the org's billing email if the serializer surfaced it.
    setEmailTo(row.organization_billing_email || "");
  };

  const sendEmail = async () => {
    if (!emailInvoice) return;
    const orgId = emailInvoice.organization_id;
    if (!orgId) {
      triggerAlert(
        "error", "Oops...",
        "This invoice is missing its organization ID — cannot email."
      );
      return;
    }
    setEmailSending(true);
    try {
      const body = emailTo.trim() ? { to: emailTo.trim() } : {};
      await axios.post(
        `/api/billing/invoices/${emailInvoice.id}/email/?organization_id=${orgId}`,
        body,
        authHeaders(),
      );
      triggerAlert("success", "Success", "Invoice emailed.");
      setEmailInvoice(null);
      setEmailTo("");
    } catch (err) {
      const msg = err?.response?.data?.error || "Could not email the invoice.";
      triggerAlert("error", "Oops...", msg);
    } finally {
      setEmailSending(false);
    }
  };

  const statusBadge = (s) => {
    const map = { paid: "btn-success activebtn", failed: "btn-danger Inactivebtn", void: "btn-secondary" };
    return (
      <span className={`btn btn-sm btn-rounded waves-effect waves-light ${map[s] || "btn-info"}`}>
        {s || "—"}
      </span>
    );
  };

  const fmtPeriod = (a, b) => {
    if (!a || !b) return "—";
    const o = { month: "short", day: "numeric" };
    return `${new Date(a).toLocaleDateString("en-US", o)} – ${new Date(b).toLocaleDateString("en-US", o)}`;
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    { name: "Invoice #", sortable: true, selector: (r) => r.invoice_number,
      cell: (r) => <span style={{ fontFamily: "monospace", fontSize: "13px" }}>{r.invoice_number}</span> },
    { name: "Organization", sortable: true, selector: (r) => r.organization_name,
      cell: (r) => <span>{r.organization_name}</span> },
    { name: "Agent / Plan", selector: (r) => r.agent_name, cell: (r) => <span>{r.agent_name}</span> },
    { name: "Period", selector: (r) => r.period_start, cell: (r) => <span>{fmtPeriod(r.period_start, r.period_end)}</span> },
    { name: "Total", selector: (r) => Number(r.total),
      cell: (r) => <span style={{ fontWeight: 600 }}>${Number(r.total || 0).toFixed(2)} {r.currency || "USD"}</span> },
    { name: "Status", selector: (r) => r.status, cell: (r) => statusBadge(r.status) },
    { name: "Date", selector: (r) => r.created_at,
      cell: (r) => <span>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</span> },
    {
      name: "Action",
      width: "250px",
      cell: (r) => (
        <div className="d-flex gap-1">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openPreview(r)}>
            Preview
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => viewPdf(r)}>
            Open
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEmail(r)}>
            Email
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    headRow: { style: { backgroundColor: "#064c77", minHeight: "52px" } },
    headCells: { style: { color: "#fff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "56px" } },
    cells: { style: { fontSize: "14px", color: "#495057" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Invoices" heading="Billing / Invoices" />
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="form-label mb-1 font_weight_500">Status</label>
                        <select className="form-select" value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="">All statuses</option>
                          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-9">
                        <label className="form-label mb-1 font_weight_500">Search</label>
                        <input type="search" className="form-control" placeholder="Invoice number or organization..."
                          value={search} onChange={(e) => setSearch(e.target.value)} />
                      </div>
                    </div>

                    {!isLoading ? (
                      <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationDefaultPage={currentPage}
                        onChangePage={(page) => { setCurrentPage(page); fetchData(page, perPage); }}
                        onChangeRowsPerPage={(newPer, page) => { setPerPage(newPer); fetchData(page, newPer); }}
                        customStyles={customStyles}
                        persistTableHead
                      />
                    ) : (
                      <div className="loader-overlay text-white"><Loader /></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {previewInvoice && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closePreview}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ height: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title">
                  Invoice {previewInvoice.invoice_number}
                </h5>
                <button type="button" className="btn-close" onClick={closePreview} />
              </div>
              <div className="modal-body p-0" style={{ overflow: "hidden" }}>
                {previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="Invoice PDF"
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <Loader />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {emailInvoice && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => !emailSending && setEmailInvoice(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Email Invoice {emailInvoice.invoice_number}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => !emailSending && setEmailInvoice(null)}
                  disabled={emailSending}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted">
                  Emails the PDF to the address below. Leave blank to use the
                  organization's billing_email on file.
                </p>
                <div className="mb-3">
                  <label className="form-label">Recipient</label>
                  <input
                    type="email"
                    className="form-control"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder={emailInvoice.organization_billing_email || "customer@example.com"}
                    disabled={emailSending}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEmailInvoice(null)}
                  disabled={emailSending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={sendEmail}
                  disabled={emailSending}
                >
                  {emailSending ? "Sending…" : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
